// client/src/components/shared/InstallApp.js — "Install the app", for the whole portal.
//
//   <InstallAppButton />   compact header button (staff/agent dashboard). Android and
//                          desktop Chrome/Edge: opens the browser's install dialog.
//                          iPhone/iPad Safari: shows "Share → Add to Home Screen".
//                          Hidden when already installed or nothing can be offered.
//   <InstallAppBanner />   the dismissible strip used at the top of the client portal.
//
// The browser's prompt is captured globally in client/src/pwa/registerServiceWorker.js.
import React, { useEffect, useRef, useState } from 'react';
import { usePwaState, promptInstall } from '../../pwa/registerServiceWorker';
import { installMode, isStandalone } from '../../pwa/pwaCore';

const DISMISS_KEY = 'brecPortalInstallDismissed';
const dismissed = () => { try { return window.localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; } };
const remember = () => { try { window.localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ } };

export const useInstallMode = () => {
    const { installPrompt, installed } = usePwaState();
    const standalone = installed || isStandalone(
        typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : false,
        typeof window !== 'undefined' ? window.navigator.standalone : false,
    );
    return installMode({
        ua: window.navigator.userAgent,
        platform: window.navigator.platform,
        maxTouchPoints: window.navigator.maxTouchPoints,
        standalone,
        hasPrompt: !!installPrompt,
    });
};

const ShareIcon = () => (
    <svg className="inline w-4 h-4 -mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M10 2l3.5 3.5-1.06 1.06L10.75 4.87V12h-1.5V4.87L7.56 6.56 6.5 5.5 10 2z" />
        <path d="M4 9h3v1.5H5.5v6h9v-6H13V9h3v9H4V9z" />
    </svg>
);

export const IosSteps = () => (
    <span>On iPhone or iPad: tap <strong>Share</strong> <ShareIcon /> at the bottom (or top) of Safari, then <strong>Add to Home Screen</strong>.</span>
);

export const InstallAppButton = ({ className = '' }) => {
    const mode = useInstallMode();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('pointerdown', close);
        return () => document.removeEventListener('pointerdown', close);
    }, [open]);
    if (mode !== 'prompt' && mode !== 'ios') return null;
    const onClick = () => (mode === 'prompt' ? promptInstall() : setOpen((o) => !o));
    return (
        <span className={`relative ${className}`} ref={ref}>
            <button type="button" onClick={onClick}
                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100">
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M10 3a1 1 0 011 1v7.59l2.3-2.3 1.4 1.42L10 15.4l-4.7-4.7 1.4-1.42 2.3 2.3V4a1 1 0 011-1z" /><path d="M4 16h12v2H4z" /></svg>
                <span>Install the app</span>
            </button>
            {open && (
                <div role="dialog" aria-label="Install on iPhone or iPad"
                    className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 shadow-lg rounded-lg p-3 text-sm text-slate-700 z-50">
                    <IosSteps />
                    <div className="mt-2 text-xs text-slate-500">The portal then opens full-screen from its own icon, and updates by itself.</div>
                </div>
            )}
        </span>
    );
};

export const InstallAppBanner = () => {
    const mode = useInstallMode();
    const [gone, setGone] = useState(dismissed);
    if (gone || (mode !== 'prompt' && mode !== 'ios')) return null;
    const close = () => { remember(); setGone(true); };
    const install = async () => { await promptInstall(); close(); };
    return (
        <div className="bg-indigo-50 border border-indigo-200 text-indigo-900 text-sm rounded-md px-4 py-3 mb-4 flex flex-wrap items-center gap-3 justify-between" role="region" aria-label="Install the app">
            <span className="flex items-center gap-2">
                <img src="/icons/icon-96.png" alt="" className="w-8 h-8 rounded" />
                {mode === 'ios' ? <IosSteps /> : <>Install the BREC portal on this device for one-tap access.</>}
            </span>
            <span className="flex gap-2">
                {mode === 'prompt' && <button type="button" onClick={install} className="px-4 py-2 min-h-[40px] bg-slate-800 text-white rounded-md">Install</button>}
                <button type="button" onClick={close} className="px-3 py-2 min-h-[40px] text-indigo-700">Not now</button>
            </span>
        </div>
    );
};
